import { Fragment, type ReactNode } from "react";

/**
 * Minimal renderer for the bubble preview strings. The copy lives in the i18n files, where JSX cannot, so the
 * two emphases WhatsApp itself renders are carried by markers:
 *
 * - `**text**` → `<b>` (textual emphasis)
 * - `` `text` `` → `<b class="font-mono">` (an amount, a reference, a masked number)
 *
 * Newlines become `<br />`. Deliberately not a Markdown parser: a translator writing an asterisk in prose
 * should not be able to reach for anything the preview cannot draw.
 */
export function renderNotificationBubbleBody(text: string): ReactNode {
  const lines = text.split("\n");
  return lines.map((line, lineIdx) => {
    const segments: ReactNode[] = [];
    let lastIndex = 0;
    const pattern = /\*\*([^*]+)\*\*|`([^`]+)`/g;
    let match: RegExpExecArray | null;
    while ((match = pattern.exec(line)) !== null) {
      if (match.index > lastIndex) {
        segments.push(line.slice(lastIndex, match.index));
      }
      if (match[1] !== undefined) {
        segments.push(<b key={`b-${lineIdx}-${match.index}`}>{match[1]}</b>);
      } else if (match[2] !== undefined) {
        segments.push(
          <b key={`m-${lineIdx}-${match.index}`} className="font-mono">
            {match[2]}
          </b>,
        );
      }
      lastIndex = pattern.lastIndex;
    }
    if (lastIndex < line.length) {
      segments.push(line.slice(lastIndex));
    }
    return (
      <Fragment key={lineIdx}>
        {segments}
        {lineIdx < lines.length - 1 && <br />}
      </Fragment>
    );
  });
}
